import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, Lock, MessageCircle, X } from "lucide-react";
import api from "@/lib/api";
import { waLink } from "@/config";
import { DuoClairLogo } from "@/components/DuoClairLogo";
import { SiteMenu } from "@/components/SiteMenu";
import {
  CATEGORIES,
  MONTHLY_FEES,
  QR_PRICES,
  WEB_EXAMPLES,
  customDetails,
  customPricing,
  euro,
  packDetails,
  socialTikTokOnly,
  socialWithTikTok,
} from "@/data/listino";

const cardBase = "text-left cursor-pointer rounded-2xl border border-white/10 bg-[#0D0D10]/75 p-5 transition-all duration-300 w-full";
const cardSel = "border-gold/60 bg-gold/[0.08] shadow-[inset_0_0_0_1px_rgba(212,160,60,0.35)]";
const chipBase = "cursor-pointer rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-zinc-200 transition-[background-color,border-color,color,box-shadow] duration-200";
const chipSel = "border-gold/70 bg-gold/[0.14] text-gold-light shadow-[inset_0_0_0_1px_rgba(212,160,60,0.45)]";

export default function ListinoPage() {
  const navigate = useNavigate();
  const [gate, setGate] = useState("loading");
  const [business, setBusiness] = useState("");

  const [ci, setCi] = useState(null);
  const [mi, setMi] = useState(0);
  const [si, setSi] = useState(-1);
  const [platforms, setPlatforms] = useState(["Instagram"]);
  const [tiktokMode, setTiktokMode] = useState("reuse");
  const [shooting, setShooting] = useState("client");
  const [completeShooting, setCompleteShooting] = useState("client");
  const [gift, setGift] = useState("volantini");
  const [customPosts, setCustomPosts] = useState(0);
  const [customReels, setCustomReels] = useState(0);
  const [customTikTokVideos, setCustomTikTokVideos] = useState(0);
  const [qrQty, setQrQty] = useState(50);
  const [qrDestination, setQrDestination] = useState("google");
  const [qrPrint, setQrPrint] = useState(false);
  const [visualIdx, setVisualIdx] = useState(null);
  const [brief, setBrief] = useState({ name: "", activity: "", target: "", style: "Da definire insieme", notes: "" });
  const [briefCopied, setBriefCopied] = useState(false);
  const [customError, setCustomError] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("dc_listino_token");
    if (!token) {
      setGate("locked");
      return;
    }
    api
      .get("/listino/access", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setBusiness(r.data.business_name || sessionStorage.getItem("dc_business") || "");
        setGate("open");
      })
      .catch(() => setGate("locked"));
  }, []);

  const state = { platforms, tiktokMode, shooting, customPosts, customReels, customTikTokVideos };
  const cat = ci !== null ? CATEGORIES[ci] : null;
  const mode = cat?.modes?.[mi];
  const social = !!mode?.platforms;
  const arr = mode?.list || cat?.list || [];

  const openCategory = (i) => {
    setCi(i);
    setMi(0);
    setSi(-1);
    setCustomError(false);
    window.scrollTo(0, 0);
  };

  const quoteRef = useRef(null);
  const customQty = social ? (socialTikTokOnly(platforms) ? customTikTokVideos : customPosts + customReels) : 0;

  const scrollToQuote = () => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setTimeout(() => {
      quoteRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    }, reduce ? 60 : 180);
  };

  const selectPlan = (i) => {
    setSi(i);
    scrollToQuote();
  };

  const confirmCustom = () => {
    if (customQty === 0) {
      setCustomError(true);
      return;
    }
    setCustomError(false);
    selectPlan(3);
  };

  const quote = useMemo(() => {
    const isCustom = social && si === 3;
    if (ci === null || si < 0 || (isCustom && customQty === 0) || (!isCustom && !arr[si])) return null;
    const pp = arr[si];
    let info = social
      ? si === 3
        ? customDetails(state)
        : packDetails(si, state)
      : { title: pp.name, price: pp.price, features: [...pp.features] };
    if (ci === 4) {
      info = { ...info, price: info.price + (completeShooting === "duoclair" ? [50, 100, 150][si] : 0) };
      info.features = info.features.map((f) =>
        completeShooting === "duoclair"
          ? f.replace("da materiale fornito dal cliente", "con riprese DuoClair").replace("montati da video forniti dal cliente", "con riprese DuoClair")
          : f
      );
    }
    const price =
      ci === 4
        ? euro(info.price) + "/mese"
        : ci === 3 && si === 2
          ? euro(QR_PRICES[qrQty])
          : !social && pp?.display
            ? pp.display
            : euro(info.price) + (social || pp?.cycle ? "/mese" : "");
    const feats = [...info.features];
    if (ci === 4)
      feats.push(
        completeShooting === "duoclair"
          ? "Riprese DuoClair: supplemento incluso nel totale, una sessione fino a 2 ore in zona; trasferte extra escluse"
          : "Riprese fornite dal cliente: nessun supplemento",
        ...(si >= 1
          ? ["Secondo omaggio scelto: " + (gift === "volantini" ? "50 volantini" : "50 card con QR personalizzato (destinazione a scelta, QR statico)")]
          : []),
        ...(si === 2 ? ["Gadget personalizzato incluso: tipologia e quantità da concordare con DuoClair"] : []),
        "Omaggi inclusi una sola volta alla prima attivazione; personalizzazioni e riordini su richiesta",
        "Una revisione per contenuto; advertising e gestione sito esclusi"
      );
    if (ci === 2)
      feats.push(
        `Canone di pubblicazione e mantenimento: ${MONTHLY_FEES[si]}/mese (separato dalla realizzazione)`,
        "Consegna tramite link con dominio personalizzato concordato",
        qrPrint
          ? "SÌ — Desidero una proposta per card QR stampate (preventivo e costo separati)"
          : "NO — Solo link, senza card stampate"
      );
    if (ci === 3 && si === 2)
      feats.push(
        `Quantità: ${qrQty} card QR personalizzate`,
        qrDestination === "google"
          ? "QR diretto alle recensioni Google — nessun canone web"
          : "QR con mini-pagina promozionale e invito alle recensioni — realizzazione su preventivo + canone tecnico mensile da concordare"
      );
    if (ci === 3 && si === 3)
      feats.push("Adesivi QR per vetrina, ingresso o banco", "Dimensioni, quantità, finitura e materiale da concordare", "Grafica personalizzata e collegamento QR da definire");
    if (ci === 2)
      feats.push(
        si === 3 ? "Consegna: tempistiche da concordare durante la call" : "Consegna indicativa: 1–2 settimane dalla ricezione dei materiali, da confermare",
        "Una revisione di correzioni inclusa prima della consegna; nuove funzioni su preventivo"
      );
    if (social)
      feats.push(
        "Canali selezionati: " + platforms.join(", "),
        shooting === "duoclair" ? "Riprese incluse nei limiti concordati; ulteriori trasferte e sessioni escluse" : "Riprese sul posto e produzioni video AI complesse escluse"
      );
    const note =
      ci === 0
        ? "Preventivo social orientativo: quantità, revisioni, costi esterni e fiscalità da confermare per iscritto."
        : ci === 1
          ? "Preventivo video AI orientativo: costi di generazione e revisioni da confermare. Pubblicazione social esclusa."
          : ci === 2
            ? "Preventivo digitale orientativo: funzioni concordate, dominio, hosting, manutenzione, dati e fiscalità da definire per iscritto."
            : "Preventivo orientativo da confermare per iscritto, inclusi costi esterni e fiscalità.";
    const title = info.title + (social ? " — " + platforms.join(" + ") : "");
    const lastText =
      "DUOCLAIR — PREVENTIVO INDICATIVO\nCategoria: " +
      cat.name +
      "\nServizio: " +
      info.title +
      "\nCanali: " +
      (social ? platforms.join(", ") : "non applicabile") +
      "\nPrezzo: " +
      price +
      "\nIncluso:\n" +
      feats.map((f) => "• " + f).join("\n") +
      "\nImporti e condizioni soggetti a conferma scritta.";
    return { title, price, features: feats, note, lastText };
  }, [ci, si, mi, platforms, tiktokMode, shooting, completeShooting, gift, customPosts, customReels, customTikTokVideos, qrQty, qrDestination, qrPrint]);

  const sendWhatsApp = () => {
    if (!quote) return;
    const message =
      "Buongiorno DuoClair, vorrei richiedere questo piano.\n\n" +
      (business ? `Attività: ${business}\n` : "") +
      quote.lastText;
    const token = sessionStorage.getItem("dc_listino_token");
    if (token) api.post("/listino/interest", { summary: message }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    window.open(waLink(message), "_blank");
  };

  const copyBrief = () => {
    const val = (v) => (v && v.trim()) || "Non indicato";
    const t = [
      "DUOCLAIR — RICHIESTA NUOVO MARCHIO",
      "Nome: " + val(brief.name),
      "Attività: " + val(brief.activity),
      "Pubblico: " + val(brief.target),
      "Stile: " + brief.style,
      "Preferenze: " + val(brief.notes),
      "Richiesta: contatto per briefing e preventivo personalizzato",
    ].join("\n");
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(t).then(() => {
        setBriefCopied(true);
        setTimeout(() => setBriefCopied(false), 2000);
      }).catch(() => window.prompt("Copia il briefing:", t));
    } else window.prompt("Copia il briefing:", t);
  };

  /* ---------- Gate ---------- */
  if (gate === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="listino-loading">
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
      </div>
    );
  }

  if (gate === "locked") {
    return (
      <div className="min-h-screen text-foreground" data-testid="listino-locked">
        <div className="relative z-10 min-h-screen flex items-center justify-center px-5">
          <div className="max-w-sm w-full bg-[#0D0D10]/80 border border-white/10 rounded-2xl p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center mb-5">
              <Lock className="w-6 h-6 text-gold" />
            </div>
            <h1 className="font-display headline-3d text-2xl mb-3">
              <span className="chrome-text">Listino </span>
              <span className="gold-text">riservato</span>
            </h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-7">
              Il listino completo è riservato a chi richiede l'analisi gratuita. Se hai già inviato la
              richiesta, la sessione può essere scaduta: invia di nuovo il modulo per sbloccare l'accesso.
            </p>
            <button
              onClick={() => navigate("/", { state: { openForm: true } })}
              data-testid="listino-locked-cta"
              className="btn-champagne w-full rounded-full px-8 py-3.5 font-bold text-sm tracking-tight hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
              Richiedi l'analisi gratuita
            </button>
            <Link to="/" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-gold-light transition-colors mt-5" data-testid="listino-locked-home">
              <ArrowLeft className="w-3.5 h-3.5" /> Torna alla home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Configurator ---------- */
  return (
    <div className="min-h-screen text-foreground" data-testid="listino-page">
      <header className="sticky top-0 z-40 bg-black/50 backdrop-blur-md border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 h-14 sm:h-16 flex items-center justify-between">
          <DuoClairLogo />
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-block font-mono text-[9px] uppercase tracking-[0.25em] text-gold/80 border border-gold/30 rounded-full px-3 py-1">
              Listino riservato
            </span>
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-xs text-zinc-300 hover:text-gold-light transition-colors" data-testid="listino-home-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Home
            </Link>
            <SiteMenu />
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-5 pb-16">
        <AnimatePresence mode="wait">
          {ci === null ? (
            <motion.main key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-8" data-testid="listino-home">
              <p className="eyebrow mb-3">Listino riservato DuoClair</p>
              <h1 className="font-display headline-3d text-3xl sm:text-4xl tracking-tight mb-3">
                <span className="chrome-text">Quale servizio vuoi </span>
                <span className="gold-text">configurare?</span>
              </h1>
              <p className="text-sm text-zinc-400 leading-relaxed mb-7 max-w-lg">
                Scegli una categoria. Vedrai soltanto le proposte relative al servizio selezionato, con
                quantità e prestazioni descritte in modo chiaro.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CATEGORIES.map((c, i) => (
                  <button
                    key={c.name}
                    onClick={() => openCategory(i)}
                    data-testid={`category-card-${i}`}
                    className={`${cardBase} hover:border-gold/50 min-h-[120px]`}
                  >
                    <span className="font-mono text-xs tracking-[0.2em] text-gold/80">0{i + 1}</span>
                    <span className="block font-sans font-extrabold text-base sm:text-lg text-slate-100 mt-3 mb-2">{c.name}</span>
                    <span className="block text-xs sm:text-[13px] text-zinc-400 leading-relaxed">{c.desc}</span>
                  </button>
                ))}
              </div>
              <div className="mt-9 flex justify-center">
                <Link
                  to="/"
                  data-testid="listino-back-home"
                  className="inline-flex items-center gap-2.5 rounded-full border border-gold/30 bg-white/[0.03] px-7 py-3 text-sm font-semibold text-gold-light/90 hover:border-gold/60 hover:bg-gold/[0.08] hover:text-gold-light transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Torna a DuoClair
                </Link>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed mt-7">
                I prezzi sono indicativi: tasse, licenze, trasferte, riprese e prestazioni extra sono da
                concordare. Le condizioni vengono sempre confermate per iscritto.
              </p>
            </motion.main>
          ) : (
            <motion.main key={`detail-${ci}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="pt-6 max-w-2xl" data-testid="listino-detail">
              <button onClick={() => { setCi(null); window.scrollTo(0, 0); }} className="text-gold-light/90 text-sm py-3 inline-flex items-center gap-2" data-testid="listino-back">
                <ArrowLeft className="w-4 h-4" /> Torna ai servizi
              </button>
              <p className="eyebrow">0{ci + 1}</p>
              <h2 className="font-display headline-3d text-2xl sm:text-3xl tracking-tight my-2">
                <span className="gold-text">{cat.name}</span>
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-4">{cat.desc}</p>

              {/* Tabs */}
              {cat.modes && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="listino-tabs">
                  {cat.modes.map((m, i) => (
                    <button
                      key={m.title}
                      onClick={() => { setMi(i); setSi(-1); setCustomError(false); }}
                      aria-pressed={i === mi}
                      data-testid={`tab-${i}`}
                      className={`rounded-xl border px-4 py-2.5 text-sm font-bold transition-all ${i === mi ? "bg-gold/85 text-black border-gold" : "border-white/15 text-zinc-300 hover:border-gold/40"}`}
                    >
                      {m.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Web delivery panel (cat 03) */}
              {ci === 2 && (
                <section className="mt-3 mb-1 rounded-2xl border border-gold/25 bg-[#0D0D10]/75 p-5" data-testid="delivery-panel">
                  <p className="eyebrow mb-2">Consegna del sito</p>
                  <h3 className="font-sans font-bold text-lg text-slate-100 mb-1">Il tuo sito, sempre a portata di mano</h3>
                  <p className="text-[13px] text-zinc-400 mb-4">Un link personalizzato da condividere su Instagram, WhatsApp e ovunque vuoi.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.25fr] gap-2.5">
                    <div className="flex items-start gap-3 p-3.5 rounded-xl border border-white/10 bg-white/[0.04]">
                      <span className="w-8 h-8 rounded-lg bg-gold/85 text-black grid place-items-center font-black text-sm shrink-0">↗</span>
                      <span>
                        <strong className="block text-sm text-slate-100">Link digitale incluso</strong>
                        <small className="block text-xs text-zinc-400 mt-1">Con dominio concordato</small>
                      </span>
                    </div>
                    <label className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${qrPrint ? "border-gold/50 bg-gold/10" : "border-white/10 bg-white/[0.04]"}`} data-testid="qr-print-addon">
                      <input type="checkbox" checked={qrPrint} onChange={(e) => setQrPrint(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[#caa879] shrink-0" />
                      <span>
                        <strong className="block text-sm text-slate-100">+ Card QR stampate</strong>
                        <small className="block text-xs text-zinc-400 mt-1">Da consegnare insieme allo scontrino: il cliente scansiona il QR e visita il tuo sito, oppure un link già esistente.</small>
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gold-light/70 mt-3">
                    {qrPrint ? "✓ Card QR aggiunte alla richiesta · costo di stampa e grafica da preventivare" : "Card QR facoltative · prezzo dedicato in base a grafica e quantità"}
                  </p>
                </section>
              )}

              {/* Complete shooting panel (cat 05) */}
              {ci === 4 && (
                <section className="mt-3 rounded-2xl border border-white/10 bg-[#0D0D10]/75 p-5" data-testid="complete-shooting-panel">
                  <h3 className="font-sans font-bold text-base text-slate-100">Chi realizza le riprese dei Reel?</h3>
                  <p className="text-[13px] text-zinc-400 mt-1 mb-4">Scegli come fornire il materiale video. I video AI restano inclusi nel piano.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {[["client", "Invio io i video · nessun supplemento"], ["duoclair", "Riprese effettuate da DuoClair"]].map(([value, label]) => (
                      <button key={value} onClick={() => setCompleteShooting(value)} aria-pressed={completeShooting === value} data-testid={`complete-shooting-${value}`} className={`${chipBase} text-left ${completeShooting === value ? chipSel : ""}`}>
                        {completeShooting === value ? "✓ " : ""}{label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gold-light/70 mt-3.5 leading-relaxed">
                    {completeShooting === "duoclair"
                      ? "Supplemento indicativo: +25 € per Reel (+50 / +100 / +150 € al mese nei tre piani). Una sessione programmata fino a 2 ore in zona; trasferte e sessioni aggiuntive su preventivo."
                      : "Il cliente registra e invia le clip. DuoClair si occupa del montaggio."}
                  </p>
                </section>
              )}

              {/* Social platform chooser */}
              {social && (
                <section className="mt-3 rounded-2xl border border-white/10 bg-[#0D0D10]/75 p-5" data-testid="platform-panel">
                  <h3 className="font-sans font-bold text-base text-slate-100">1. Dove vuoi pubblicare?</h3>
                  <p className="text-[13px] text-zinc-400 mt-1 mb-4">Seleziona uno o più social. Instagram e Facebook condividono lo stesso pacchetto, senza sovrapprezzo.</p>
                  <div className="flex flex-wrap gap-2">
                    {["Instagram", "Facebook", "TikTok"].map((name) => (
                      <button
                        key={name}
                        onClick={() => {
                          setPlatforms((p) => {
                            if (p.includes(name)) {
                              if (p.length === 1) return p;
                              return p.filter((x) => x !== name);
                            }
                            return [...p, name];
                          });
                          setSi(-1);
                          setCustomError(false);
                        }}
                        aria-pressed={platforms.includes(name)}
                        data-testid={`platform-${name.toLowerCase()}`}
                        className={`${chipBase} ${platforms.includes(name) ? chipSel : ""}`}
                      >
                        {platforms.includes(name) ? "✓ " : "+ "}{name}
                      </button>
                    ))}
                  </div>
                  {socialWithTikTok(platforms) ? (
                    <div className="mt-5 pt-4 border-t border-white/10 grid gap-2.5">
                      <h3 className="font-sans font-bold text-[15px] text-slate-100">2. Come vuoi gestire i video TikTok?</h3>
                      <button onClick={() => setTiktokMode("reuse")} aria-pressed={tiktokMode === "reuse"} data-testid="tiktok-reuse" className={`${cardBase} !p-4 ${tiktokMode === "reuse" ? cardSel : ""}`}>
                        <strong className="block text-[15px] text-slate-100">Riutilizza gli stessi Reel · +0 €</strong>
                        <small className="block text-xs text-zinc-400 mt-1.5 leading-relaxed">Nessun supplemento. Gli stessi video di Instagram/Facebook, con descrizioni e pubblicazione adattate per TikTok. Nessun Reel aggiuntivo.</small>
                      </button>
                      <button onClick={() => setTiktokMode("double")} aria-pressed={tiktokMode === "double"} data-testid="tiktok-double" className={`${cardBase} !p-4 ${tiktokMode === "double" ? cardSel : ""}`}>
                        <strong className="block text-[15px] text-slate-100">Voglio il doppio dei video su TikTok</strong>
                        <small className="block text-xs text-zinc-400 mt-1.5 leading-relaxed">Reel già previsti + nuovi montaggi da clip che ci invii. Supplementi: +70 / +100 / +150 € nei tre pacchetti.</small>
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gold-light/70 mt-4">
                      {socialTikTokOnly(platforms) ? "Gestione solo TikTok: pacchetti esclusivamente video, senza post grafici." : "Instagram e Facebook insieme: nessun supplemento."}
                    </p>
                  )}
                </section>
              )}

              {/* Social shooting panel */}
              {social && (
                <section className="mt-3 rounded-2xl border border-white/10 bg-[#0D0D10]/75 p-5" data-testid="shooting-panel">
                  <h3 className="font-sans font-bold text-base text-slate-100">Chi realizza le riprese dei Reel?</h3>
                  <p className="text-[13px] text-zinc-400 mt-1 mb-4">Scegli chi registra il materiale video. Il montaggio resta incluso nei pacchetti.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {[["client", "I video li fornisco io"], ["duoclair", "Voglio le riprese DuoClair"]].map(([value, label]) => (
                      <button key={value} onClick={() => setShooting(value)} aria-pressed={shooting === value} data-testid={`shooting-${value}`} className={`${chipBase} text-left ${shooting === value ? chipSel : ""}`}>
                        {shooting === value ? "✓ " : ""}{label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gold-light/70 mt-3.5 leading-relaxed">
                    {shooting === "duoclair"
                      ? "Supplemento orientativo: +25 € per video da riprendere; riprese in un’unica visita programmata fino a 2 ore, in zona. Trasferte e ulteriori sessioni escluse."
                      : "Nessun supplemento: il negozio registra e invia le clip seguendo le nostre indicazioni."}
                  </p>
                </section>
              )}

              {(mode?.hint || cat.hint) && (
                <p className="text-[12px] text-zinc-500 leading-relaxed border-l-2 border-gold/40 pl-3 mt-5">{mode?.hint || cat.hint}</p>
              )}

              {/* Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="options-grid">
                {arr.map((p, i) => {
                  let info = social ? packDetails(i, state) : { title: p.name, price: p.price, features: p.features };
                  if (ci === 4 && completeShooting === "duoclair") info = { ...info, price: p.price + [50, 100, 150][i] };
                  if (ci === 3 && i === 2) info = { ...info, price: QR_PRICES[qrQty] };
                  let feats = info.features;
                  if (ci === 4)
                    feats = feats.map((f) =>
                      f.includes("Reel mensili da materiale fornito dal cliente") || f.includes("Reel mensili montati da video forniti dal cliente")
                        ? completeShooting === "duoclair"
                          ? f.replace("da materiale fornito dal cliente", "con riprese DuoClair").replace("montati da video forniti dal cliente", "con riprese DuoClair")
                          : f
                        : f
                    );
                  const priceLabel = social
                    ? euro(info.price) + "/mese"
                    : ci === 4
                      ? euro(info.price) + "/mese"
                      : ci === 3 && i === 2
                        ? euro(info.price)
                        : p.display || euro(info.price) + (p.cycle ? "/mese" : "");
                  const subtitle = social
                    ? shooting === "duoclair"
                      ? "Contenuti mensili con riprese effettuate da DuoClair."
                      : "Contenuti mensili con materiale video fornito dal cliente."
                    : p.summary || "Prestazioni incluse:";
                  const card = (
                    <button
                      onClick={() => selectPlan(i)}
                      aria-pressed={si === i}
                      data-testid={`choice-${i}`}
                      className={`${cardBase} ${si === i ? cardSel : ""} hover:border-gold/40`}
                    >
                      <strong className="block font-sans text-[15px] sm:text-base text-slate-100">{info.title}</strong>
                      <div className="font-display text-2xl sm:text-[26px] gold-text my-2.5" data-testid={`choice-price-${i}`}>{priceLabel}</div>
                      <p className="text-xs text-zinc-400 leading-relaxed mb-2">{subtitle}</p>
                      <ul className="space-y-1.5 mt-2">
                        {feats.map((f) => (
                          <li key={f} className="flex gap-2 text-[12px] text-zinc-300 leading-snug">
                            <span className="text-gold font-bold">✓</span>
                            {f}
                          </li>
                        ))}
                      </ul>
                      {ci === 2 && <div className="text-[13px] text-gold-light/80 mt-2">Canone tecnico: <strong>{MONTHLY_FEES[i]}</strong>/mese</div>}
                    </button>
                  );
                  if (ci === 2) {
                    return (
                      <div key={i} className={`rounded-2xl border overflow-hidden flex flex-col ${si === i ? "border-gold/60 bg-gold/[0.06]" : "border-white/10 bg-[#0D0D10]/75"}`}>
                        {card}
                        <button
                          onClick={() => setVisualIdx(i)}
                          data-testid={`preview-${i}`}
                          className="w-full text-left border-t border-gold/20 bg-gold/[0.12] text-gold-light px-5 py-3.5 text-sm font-bold hover:bg-gold/20 transition-colors"
                        >
                          → Guarda esempio visivo dell'offerta
                        </button>
                      </div>
                    );
                  }
                  return <div key={i}>{card}</div>;
                })}

                {/* Custom social package */}
                {social && (
                  <section className={`sm:col-span-2 rounded-2xl border p-5 ${si === 3 ? "border-gold/60 bg-gold/[0.06]" : "border-gold/25 bg-[#0D0D10]/75"}`} data-testid="custom-panel">
                    <p className="eyebrow mb-2">Pacchetto 4 · Personalizzato</p>
                    <h3 className="font-sans font-bold text-lg text-slate-100">{socialTikTokOnly(platforms) ? "Crea il tuo pacchetto TikTok" : "Crea il tuo pacchetto su misura"}</h3>
                    <p className="text-[13px] text-zinc-400 mt-1 mb-4">Scegli liberamente il numero di post e Reel, anche partendo da zero. Il prezzo indicativo si aggiorna automaticamente. Le tariffe dei pacchetti fissi restano separate.</p>
                    {socialTikTokOnly(platforms) ? (
                      <RangeField title="Video mensili" max={30} value={customTikTokVideos} unit="video" onChange={(v) => { setCustomTikTokVideos(v); setCustomError(false); }} testid="range-tiktok" />
                    ) : (
                      <>
                        <RangeField title="Post mensili" max={30} value={customPosts} unit="post" onChange={(v) => { setCustomPosts(v); setCustomError(false); }} testid="range-posts" />
                        <RangeField title="Reel mensili" max={20} value={customReels} unit="Reel" onChange={(v) => { setCustomReels(v); setCustomError(false); }} testid="range-reels" />
                      </>
                    )}
                    <div className="font-display text-2xl gold-text my-4" data-testid="custom-total">{euro(customPricing(state))}/mese</div>
                    {customError && (
                      <p className="text-[13px] text-gold-light leading-relaxed mb-3" role="alert" data-testid="custom-error">
                        Scegli almeno un contenuto per creare il tuo pacchetto personalizzato.
                      </p>
                    )}
                    <button
                      onClick={confirmCustom}
                      data-testid="custom-select"
                      className="w-full rounded-xl bg-gold/85 text-black font-extrabold py-3.5 text-sm hover:bg-gold transition-colors"
                    >
                      Seleziona pacchetto personalizzato
                    </button>
                  </section>
                )}

                {/* Brand brief (cat 01, mode 2, offer 3) */}
                {ci === 0 && mi === 1 && si === 2 && (
                  <section className="sm:col-span-2 rounded-2xl border border-gold/25 bg-[#0D0D10]/75 p-5 grid gap-3.5" data-testid="brand-brief">
                    <div>
                      <p className="eyebrow mb-2">Briefing nuovo marchio</p>
                      <h3 className="font-sans font-bold text-lg text-slate-100">Raccontaci la tua nuova attività</h3>
                      <p className="text-[13px] text-zinc-400 mt-1">Compila i campi per preparare una prima call: puoi copiare il riepilogo e condividerlo tu con noi.</p>
                    </div>
                    <BriefField label="Nome del progetto o nome provvisorio">
                      <input value={brief.name} onChange={(e) => setBrief({ ...brief, name: e.target.value })} placeholder="Es. Pulito & Co." className="input-lux !h-11 text-sm w-full" data-testid="bf-name" />
                    </BriefField>
                    <BriefField label="Che attività vuoi aprire?">
                      <textarea value={brief.activity} onChange={(e) => setBrief({ ...brief, activity: e.target.value })} rows={3} placeholder="Racconta servizi, prodotti e idea di partenza" className="input-lux !h-auto py-3 text-sm w-full" data-testid="bf-activity" />
                    </BriefField>
                    <BriefField label="A chi vuoi rivolgerti?">
                      <textarea value={brief.target} onChange={(e) => setBrief({ ...brief, target: e.target.value })} rows={2} placeholder="Clienti ideali, zona, tipo di mercato" className="input-lux !h-auto py-3 text-sm w-full" data-testid="bf-target" />
                    </BriefField>
                    <BriefField label="Che stile immagini?">
                      <select value={brief.style} onChange={(e) => setBrief({ ...brief, style: e.target.value })} className="input-lux !h-11 text-sm w-full" data-testid="bf-style">
                        {["Da definire insieme", "Elegante e premium", "Moderno e minimale", "Giovane e dinamico", "Tradizionale e affidabile"].map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </BriefField>
                    <BriefField label="Colori, esempi o preferenze (facoltativo)">
                      <textarea value={brief.notes} onChange={(e) => setBrief({ ...brief, notes: e.target.value })} rows={2} placeholder="Colori preferiti, marchi di ispirazione, cosa evitare" className="input-lux !h-auto py-3 text-sm w-full" data-testid="bf-notes" />
                    </BriefField>
                    <button onClick={copyBrief} data-testid="copy-brief" className="rounded-xl bg-gold/85 text-black font-extrabold py-3.5 text-sm hover:bg-gold transition-colors">
                      {briefCopied ? "Briefing copiato!" : "Copia richiesta per la call"}
                    </button>
                    <small className="text-[11px] text-zinc-500 leading-relaxed">Il prezzo parte da 250 € per un progetto di identità visiva essenziale. Complessità, varianti e materiali aggiuntivi verranno concordati durante la call.</small>
                  </section>
                )}

                {/* Omaggi di benvenuto (cat 05) */}
                {ci === 4 && (
                  <section className="sm:col-span-2 rounded-2xl border border-gold/25 bg-[#0D0D10]/75 p-5" data-testid="brandkit-panel">
                    <p className="eyebrow mb-2">Omaggi di benvenuto</p>
                    <h3 className="font-sans font-bold text-lg text-slate-100 mb-2">3 magliette personalizzate in ogni piano.</h3>
                    <p className="text-[13px] text-zinc-400 leading-relaxed">Con Crescita e Partner scegli anche 50 volantini oppure 50 card con QR personalizzato: il QR può portare a sito, social o pagina recensioni (QR statico, salvo accordi). Con Partner anche un gadget da concordare con DuoClair.</p>
                    <p className="text-[13px] text-zinc-400 leading-relaxed mt-2">Omaggi inclusi una sola volta alla prima attivazione. Personalizzazioni e riordini aggiuntivi su richiesta.</p>
                    {si >= 1 && (
                      <div className="mt-4 pt-4 border-t border-white/10" data-testid="gift-panel">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80 mb-2.5">Il tuo secondo omaggio</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[["volantini", "50 volantini"], ["card", "50 card con QR"]].map(([val, label]) => (
                            <button key={val} onClick={() => setGift(val)} aria-pressed={gift === val} data-testid={`gift-${val}`} className={`${chipBase} text-center ${gift === val ? chipSel : ""}`}>
                              {gift === val ? "✓ " : ""}{label}
                            </button>
                          ))}
                        </div>
                        {si === 2 && <p className="text-xs text-gold-light/70 mt-3 leading-relaxed">Gadget personalizzato incluso: tipologia e quantità concordate direttamente con DuoClair.</p>}
                      </div>
                    )}
                  </section>
                )}

                {/* QR card config (cat 04, offer 3) */}
                {ci === 3 && si === 2 && (
                  <section className="sm:col-span-2 rounded-2xl border border-gold/25 bg-[#0D0D10]/75 p-5 grid gap-3.5" data-testid="qr-config">
                    <div>
                      <p className="eyebrow mb-2">Configura le tue card</p>
                      <h3 className="font-sans font-bold text-lg text-slate-100">Quante card vuoi stampare?</h3>
                      <p className="text-[13px] text-zinc-400 mt-1">La card non richiede l'acquisto di un sito: scegli quantità e destinazione del QR.</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[[50, 29], [100, 39], [250, 79]].map(([n, p]) => (
                        <button key={n} onClick={() => setQrQty(n)} aria-pressed={qrQty === n} data-testid={`qr-qty-${n}`} className={`${chipBase} text-center !px-2 ${qrQty === n ? chipSel : ""}`}>
                          {n} card · {euro(p)}
                        </button>
                      ))}
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">Dove porta il QR?</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[["google", "Solo recensione Google"], ["promo", "Mini-pagina promozionale"]].map(([val, label]) => (
                        <button key={val} onClick={() => setQrDestination(val)} aria-pressed={qrDestination === val} data-testid={`qr-dest-${val}`} className={`${chipBase} text-center ${qrDestination === val ? chipSel : ""}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[13px] text-zinc-400 leading-relaxed">
                      {qrDestination === "google"
                        ? "Apre direttamente il modulo per scrivere una recensione Google. Nessun canone per la pagina web."
                        : "Pagina dedicata con invito alle recensioni e codice promozionale. Sviluppo su preventivo e canone tecnico mensile separato da definire prima dell'ordine."}
                    </p>
                  </section>
                )}
              </div>

              {/* Quote */}
              <AnimatePresence>
                {quote && (
                  <motion.div
                    key="quote"
                    ref={quoteRef}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-5 scroll-mt-20 sm:scroll-mt-24 rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/[0.12] to-white/[0.03] p-5 sm:p-6"
                    data-testid="quote"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Riepilogo orientativo</span>
                    <h3 className="font-sans font-extrabold text-lg sm:text-xl text-slate-100 mt-2.5" data-testid="quote-title">{quote.title}</h3>
                    <div className="font-display text-3xl sm:text-4xl gold-text my-3" data-testid="quote-price">{quote.price}</div>
                    <ul className="space-y-1.5">
                      {quote.features.map((f) => (
                        <li key={f} className="flex gap-2 text-[12.5px] text-zinc-200 leading-snug">
                          <span className="text-gold font-bold">✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-zinc-500 leading-relaxed mt-4">{quote.note}</p>
                    <button
                      onClick={sendWhatsApp}
                      data-testid="send-wa-button"
                      className="btn-champagne mt-5 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-bold text-sm tracking-tight hover:scale-[1.02] active:scale-[0.98] transition-transform"
                    >
                      <MessageCircle className="w-4 h-4" /> Richiedi questo piano
                    </button>
                    <p className="text-[11px] text-zinc-500 mt-3">Si apre WhatsApp con il riepilogo già compilato: controlli il messaggio e decidi tu se inviarlo.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.main>
          )}
        </AnimatePresence>
      </div>

      {/* Visual example modal */}
      <AnimatePresence>
        {visualIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/85 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && setVisualIdx(null)}
            data-testid="visual-modal"
          >
            <div className="relative w-full max-w-2xl max-h-[92vh] overflow-auto rounded-2xl border border-gold/30 bg-[#0B0B0D]">
              <button onClick={() => setVisualIdx(null)} aria-label="Chiudi anteprima" data-testid="visual-close" className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full border border-gold/40 bg-black/70 text-slate-100 grid place-items-center hover:border-gold transition-colors">
                <X className="w-5 h-5" />
              </button>
              <div className="p-5 pb-2">
                <p className="eyebrow mb-1.5">Esempio visivo dell'offerta</p>
                <h3 className="font-sans font-bold text-lg text-slate-100">{WEB_EXAMPLES[visualIdx].title}</h3>
                <p className="text-xs text-zinc-400 mt-1">{WEB_EXAMPLES[visualIdx].label}</p>
              </div>
              <div className="px-5 pb-5">
                <img src={WEB_EXAMPLES[visualIdx].src} alt={`Esempio visivo ${WEB_EXAMPLES[visualIdx].title}`} className="w-full h-auto rounded-xl border border-gold/25" loading="lazy" />
                <p className="text-xs text-zinc-500 mt-3 leading-relaxed">Anteprima dimostrativa: grafica, contenuti e struttura vengono adattati in base all'attività prima della realizzazione.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const RangeField = ({ title, max, value, unit, onChange, testid }) => (
  <div className="my-4">
    <div className="flex justify-between text-sm">
      <span className="text-zinc-300">{title}</span>
      <strong className="text-gold-light">{value} {unit}</strong>
    </div>
    <input
      type="range"
      min={0}
      max={max}
      step={1}
      value={value}
      aria-label={title}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full mt-3 accent-[#c7a676] cursor-pointer"
      data-testid={testid}
    />
  </div>
);

const BriefField = ({ label, children }) => (
  <label className="grid gap-1.5 text-sm font-semibold text-zinc-200">
    {label}
    {children}
  </label>
);
